import { useEffect, useState } from 'react';
import { TOLERANCIA_MENOR } from '../../../utils/caja';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Wallet, AlertTriangle, CircleDollarSign, Banknote } from 'lucide-react';
import { getFinanceOverview, FinanceOverview, getDebts, Debt, moneyEntero as money } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { teacherPhotoUrl } from '../../teachers/api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
/** «18 jul, 23:07» a partir del datetime del API. */
function fmtFecha(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MESES[d.getMonth()]}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function Money({ v, tone }: { v: number; tone?: 'in' | 'out' | 'net' }) {
  const cls = tone === 'in' ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'out' ? 'text-red-600 dark:text-red-400'
    : v < 0 ? 'text-red-600 dark:text-red-400' : 'text-content';
  return <span className={`font-display font-bold tabular-nums ${cls}`}>{money(v)}</span>;
}

function PeriodCard({ title, income, expense, net }: { title: string; income: number; expense: number; net: number }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-content-3">{title}</div>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-content-2"><TrendingUp size={14} className="text-emerald-500" /> Ingresos</span>
          <Money v={income} tone="in" />
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-content-2"><TrendingDown size={14} className="text-red-500" /> Egresos</span>
          <Money v={expense} tone="out" />
        </div>
        <div className="mt-1 flex items-center justify-between border-t border-line pt-2 text-sm">
          <span className="font-medium text-content">Neto</span>
          <span className="text-lg"><Money v={net} tone="net" /></span>
        </div>
      </div>
    </div>
  );
}

const SEMAFORO = {
  ok: { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-500/20 border-emerald-500/30', label: 'Caja cuadró' },
  warn: { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-500/20 border-amber-500/30', label: `Diferencia menor (≤ $${TOLERANCIA_MENOR})` },
  bad: { dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400', ring: 'ring-red-500/20 border-red-500/30', label: 'Faltante o sobrante' },
};

export default function OverviewPanel() {
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [d, setD] = useState<FinanceOverview | null>(() => readCache<FinanceOverview>('fin-overview') ?? null);
  const [debts, setDebts] = useState<Debt[] | null>(() => readCache<{ debts: Debt[] }>('receivables')?.debts ?? null);
  useEffect(() => {
    getFinanceOverview().then((r) => { setD(r); writeCache('fin-overview', r); })
      .catch(() => setD((prev) => prev ?? null));
    getDebts().then((r) => setDebts(r.data)).catch(() => setDebts((prev) => prev ?? []));
  }, []);

  // Deudores agrupados (mismo criterio que «Por cobrar» del Inicio) + adeudos sin alumno vinculado.
  const debtores = (() => {
    if (!debts) return { rows: [] as { id: number; name: string; has_photo: number; total: number; maestro: boolean; profe: boolean }[], sinFicha: 0, sinFichaTotal: 0 };
    const m = new Map<string, { id: number; name: string; has_photo: number; total: number; maestro: boolean; profe: boolean }>();
    let sinFicha = 0, sinFichaTotal = 0;
    for (const x of debts) {
      if (x.settled) continue;
      // `remaining` (importe − abonos), NO `amount`: si no, quien ya abonó sigue contando completo
      // y este resumen no cuadra con «Por cobrar» del Inicio (que ya usa el saldo, commit b929824).
      // Caso real 15-ago: aquí salía $6,150 y en el Inicio $5,300 — la diferencia eran los $800
      // abonados por Mónica Villalobos y los $50 de Alika Navarro.
      // Maestros (fiados de vitrina) también se listan, con su nombre y enlace (Carlos 29-sep).
      const esMaestro = !x.contact_id && !!x.payer_user_id;
      if (!x.contact_id && !esMaestro) { sinFicha++; sinFichaTotal += Number(x.remaining ?? x.amount) || 0; continue; }
      const key = esMaestro ? `u-${x.payer_user_id}` : `c-${x.contact_id}`;
      const prev = m.get(key) || { id: esMaestro ? Number(x.payer_user_id) : Number(x.contact_id), name: x.student_name, has_photo: x.has_photo, total: 0, maestro: esMaestro, profe: esMaestro && !!x.payer_es_profe };
      prev.total += Number(x.remaining ?? x.amount) || 0;
      m.set(key, prev);
    }
    return { rows: [...m.values()].sort((a, b) => b.total - a.total), sinFicha, sinFichaTotal };
  })();

  if (!d) return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>;

  const cb = d.cashbox;
  const sem = cb.status === 'closed' ? SEMAFORO[cb.semaforo] : null;

  return (
    <div className="space-y-4">
      {/* Ingresos vs egresos: hoy y mes */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PeriodCard title="Hoy" income={d.today.income} expense={d.today.expense} net={d.today.net} />
        <PeriodCard title="Mes en curso" income={d.month.income} expense={d.month.expense} net={d.month.net} />
      </div>

      {/* Deudas + Caja */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Estatus de deudas: total + quiénes deben (clic → ficha) */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-content-3"><CircleDollarSign size={14} /> Deudas</div>
          <div className="flex items-end justify-between">
            <div>
              <div className="font-display text-2xl font-bold text-content">{money(d.debts.total_open)}</div>
              <div className="text-xs text-content-3">{d.debts.count_open} adeudo(s) · {d.debts.students_open} alumno(s)</div>
            </div>
            {d.debts.overdue_count > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-right">
                <div className="flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400"><AlertTriangle size={12} /> Vencidas</div>
                <div className="text-sm font-bold text-red-600 dark:text-red-400">{money(d.debts.overdue_total)}</div>
                <div className="text-[10px] text-content-3">{d.debts.overdue_count} adeudo(s)</div>
              </div>
            )}
          </div>
          {debtores.rows.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-line pt-3">
              {debtores.rows.slice(0, 6).map((s) => (
                // Equipo que no da clases: sin ficha de profesor → sin enlace (antes 404 y carga eterna, 2-oct-2026).
                <Link key={`${s.maestro ? 'u' : 'c'}-${s.id}`} to={s.maestro ? (s.profe ? `/profesores/${s.id}` : '#') : `/alumnos/${s.id}`}
                  onClick={s.maestro && !s.profe ? (e) => e.preventDefault() : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-1 py-1 transition ${s.maestro && !s.profe ? 'cursor-default' : 'hover:bg-surface-2'}`}>
                  {s.maestro
                    ? <PersonAvatar userId={s.id} hasPhoto={s.has_photo} photoSrc={teacherPhotoUrl(s.id)} name={s.name} size="h-7 w-7" initialClass="text-[11px]" />
                    : <PersonAvatar contactId={s.id} hasPhoto={s.has_photo} name={s.name} size="h-7 w-7" initialClass="text-[11px]" />}
                  <span className="min-w-0 flex-1 truncate text-sm text-content">
                    {s.name}
                    {s.maestro && <>{' '}<span className="ml-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">{s.profe ? 'maestro' : 'equipo'}</span></>}
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-content">{money(s.total)}</span>
                </Link>
              ))}
              {debtores.sinFicha > 0 && (
                <li className="px-1 pt-1 text-[11px] text-content-3">
                  +{debtores.sinFicha} adeudo(s) sin alumno vinculado ({money(debtores.sinFichaTotal)}) — ver en Adeudos
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Estatus de caja (semáforo) */}
        <div className={`rounded-2xl border bg-surface p-4 shadow-sm ${sem ? `ring-1 ${sem.ring}` : 'border-line'}`}>
          <div className="mb-3 flex items-center justify-between gap-1.5 text-xs font-semibold uppercase tracking-wide text-content-3">
            <span className="flex items-center gap-1.5"><Wallet size={14} /> Caja</span>
            <Link to="/caja" className="font-medium normal-case tracking-normal text-accent-ink hover:underline">Ir a Caja →</Link>
          </div>
          {cb.status === 'none' && <div className="text-sm text-content-3">Sin caja registrada.</div>}
          {cb.status === 'open' && (
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Caja abierta</div>
              <div className="mt-1 text-content-2 text-sm">Esperado en caja: <Money v={cb.expected_amount} /></div>
            </div>
          )}
          {cb.status === 'closed' && sem && (
            <div>
              <div className={`flex items-center gap-2 text-sm font-semibold ${sem.text}`}><span className={`h-2.5 w-2.5 rounded-full ${sem.dot}`} /> {sem.label}</div>
              {/* Sin fecha parecía la caja de HOY (confundió a Carlos 28-jul): es la ÚLTIMA cerrada. */}
              <div className="mt-0.5 text-xs text-content-3">Última caja registrada · cerró el {fmtFecha(cb.closed_at)}</div>
              <div className="mt-1 flex items-center gap-3 text-sm text-content-2">
                <span className="flex items-center gap-1"><Banknote size={13} /> Contado: <b className="text-content">{money(cb.close_amount)}</b></span>
                <span>Esperado: <b className="text-content">{money(cb.expected_amount)}</b></span>
              </div>
              {cb.difference !== 0 && <div className={`mt-0.5 text-xs ${sem.text}`}>Diferencia: {cb.difference > 0 ? '+' : ''}{money(cb.difference)}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
